import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  OverlayTrigger,
  Spinner,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
// components
import { ReactComponent as MagnifyingGlass } from "assets/svg/magnifying-glass.svg";
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-gray-16.svg";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlineUsersMore } from "assets/svg/outline-users-more.svg";
import { ReactComponent as OutlineArrowUp16 } from "assets/svg/outline-arrow-up-16.svg";
import { ReactComponent as OutlineArrowDown16 } from "assets/svg/outline-arrow-down-16.svg";
import { ReactComponent as OutlineCopy20 } from "assets/svg/outline-copy-20.svg";
import { ReactComponent as OutlineCopied20 } from "assets/svg/outline-copied-20.svg";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import ErrorMessage from "components/error-message";
import LazyImage from "components/lazy-image";
import PatientCreatorContainer from "components/patient-editor/PatientCreatorContainer";
import ClientStatusTag from "./components/client-status-tag/ClientStatusTag";
import PatientTypeTag from "./components/patient-type-tag/PatientTypeTag";
// hooks
import { useUser } from "contexts/UserContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useSubscription } from "contexts/subscription-context";
import capitalize from "utils/capitalize";
// styles
import getClasses from "utils/getClasses";
import styles from "./PatientCharts.module.scss";
// constants
import { DOB_FORMAT } from "components/patient-editor/constants/dobFormat";
// services
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ENABLE_FAMILY_TAG,
  ENABLE_MERGE_CLIENTS,
  ENABLE_MULTIPLE_CLINICIANS,
  ENABLE_SWITCH_CLIENT_TYPE,
} from "constants/featureFlags";
import { useClientsContext } from "contexts/ClientsContext";
import { useTimeOnPageContext } from "contexts/time-on-page";
import _ from "lodash";
import {
  PATIENT_TYPE,
  patientChartInfiniteQueryOptions,
} from "queries/patientChartQueryOptions";
import useFetchAllPagesForInfiniteQuery from "queries/useFetchAllPagesForInfiniteQuery";
import putPatientProfile from "services/patient-chart/putPatientProfile";
import { AmplitudePageName, debouncedTrack, track } from "utils/amplitude";
import { apiHostAndPath } from "utils/api";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const CLINICIANS = [
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Smith",
  },
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Johnson",
  },
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Williams",
  },
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Brown",
  },
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Davis",
  },
  {
    orchid_pro_user_id: "bb79a762-144a-4085-8475-22d4b0ba947a",
    name: "Dr. Miller",
  },
];

const PATIENT_CHARTS_EMPTY_STATE = "/img/client-charts-empty.png";

const PATIENT_CONTACT_TABS = {
  individual: { id: "individual", titleStringId: "Individual" },
  couple: { id: "couple", titleStringId: "Couple" },
};

const PATIENT_CONTACT_TAB_AMHC = {
  individual: { id: "individual", titleStringId: "Individual" },
};

const MARK_AS_DISCHARGED_DROPDOWN_ITEM_LABEL =
  "mark-patient-as-discharged-dropdown-item";
const ADD_PATIENT_TO_COUPLE_DROPDOWN_ITEM_LABEL =
  "add-patient-to-couple-dropdown-item";
const MARK_AS_INACTIVE_DROPDOWN_ITEM_LABEL =
  "mark-patient-as-inactive-dropdown-item";
const MARK_AS_ACTIVE_DROPDOWN_ITEM_LABEL =
  "mark-patient-as-active-dropdown-item";
const MARK_AS_WAITLIST_DROPDOWN_ITEM_LABEL =
  "mark-patient-as-waitlist-dropdown-item";
const MERGE_WITH_ANOTHER_PATIENT_DROPDOWN_ITEM_LABEL =
  "merge-with-another-patient-dropdown-item";
const SWITCH_CLIENT_TYPE_DROPDOWN_ITEM_LABEL =
  "switch-client-type-dropdown-item";

const statuses = [
  {
    label: "All",
    status: "all" as const,
    trackLabel: "all-filter",
  },
  {
    label: "Active",
    status: "patient" as const,
    trackLabel: "active-filter",
  },
  {
    label: "Waitlist",
    status: "waitlist" as const,
    trackLabel: "waitlist-filter",
  },
  {
    label: "Inactive",
    status: "inactive" as const,
    trackLabel: "inactive-filter",
  },
  {
    label: "Discharged",
    status: "discharged" as const,
    trackLabel: "discharged-filter",
  },
] as const;

const patientTypes = [
  {
    label: "Adult",
    type: "adult" as const,
    trackLabel: "adult-type-filter",
  },
  {
    label: "Minor",
    type: "minor" as const,
    trackLabel: "minor-type-filter",
  },
] as const;

type FilterStatus = typeof statuses[number]["status"];
type FilterPatientType = typeof patientTypes[number]["type"];

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "patient-charts";

const PatientCharts = () => {
  const { setPage: timeOnPageSetPage } = useTimeOnPageContext();
  useEffect(() => timeOnPageSetPage(AMPLITUDE_PAGE_NAME), [timeOnPageSetPage]);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [searchTerm, setSearchTerm] = useState("");

  const location = useLocation();

  const { refreshClients } = useClientsContext();

  const [clientStatus, setClientStatus] = useState<FilterStatus[]>(() => {
    const clientStatusParam = new URLSearchParams(location.search).get(
      "client-status",
    );
    switch (clientStatusParam) {
      case "patient":
      case "waitlist":
      case "inactive":
      case "discharged":
        return [clientStatusParam as FilterStatus];
      default:
        return ["patient"];
    }
  });

  const [selectedPatientTypes, setSelectedPatientTypes] = useState<
    FilterPatientType[]
  >(["adult", "minor"]);

  const [patientType, setPatientType] = useState<PATIENT_TYPE>(() => {
    const patientTypeParam = new URLSearchParams(location.search).get("tab");
    switch (patientTypeParam) {
      case "individual":
      case "couple":
        return patientTypeParam;
      default:
        return "individual";
    }
  });

  const getValidClientStatus = (
    status: string,
  ): "patient" | "waitlist" | "inactive" | "discharged" => {
    if (["patient", "waitlist", "inactive", "discharged"].includes(status)) {
      return status as "patient" | "waitlist" | "inactive" | "discharged";
    }
    return "patient";
  };

  const toggleClientStatus = (status: FilterStatus) => {
    setClientStatus((prev) => {
      if (status === "all") {
        const hasAllIndividualStatuses = [
          "patient",
          "waitlist",
          "inactive",
          "discharged",
        ].every((s) => prev.includes(s as FilterStatus));

        if (hasAllIndividualStatuses) {
          return ["all"];
        } else {
          return ["patient", "waitlist", "inactive", "discharged"];
        }
      }

      const newStatus = prev.filter((s) => s !== "all");

      if (newStatus.includes(status)) {
        const filtered = newStatus.filter((s) => s !== status);
        return filtered.length === 0 ? ["all"] : filtered;
      } else {
        return [...newStatus, status];
      }
    });
  };

  const togglePatientType = (type: FilterPatientType) => {
    setSelectedPatientTypes((prev) => {
      if (prev.includes(type)) {
        const filtered = prev.filter((t) => t !== type);
        return filtered.length === 0 ? ["adult", "minor"] : filtered;
      } else {
        return [...prev, type];
      }
    });
  };

  const { practiceOwnerOrchidUserId } = useTeamContext();

  const patientChartQuery = useInfiniteQuery(
    patientChartInfiniteQueryOptions({
      clientStatus: ["patient", "waitlist", "inactive", "discharged"],
      patientType,
      search: searchTerm,
      sortBy: "first_name",
      ...(practiceOwnerOrchidUserId && {
        onBehalfOf: practiceOwnerOrchidUserId,
      }),
    }),
  );
  useFetchAllPagesForInfiniteQuery(patientChartQuery);

  const patientChartEntries = useMemo(() => {
    const allEntries =
      patientChartQuery.data?.pages?.flatMap((page) => page.entries) ?? [];

    // Filter entries based on the selected client status
    const filteredByStatus = clientStatus.includes("all")
      ? allEntries
      : allEntries.filter((entry) => {
          const entryClientStatus = entry.patient.client_status;
          return clientStatus.includes(entryClientStatus as FilterStatus);
        });

    // Filter entries based on the selected patient types
    const filteredByType = filteredByStatus.filter((entry) => {
      const isMinor = entry.guardians?.length && entry.guardians.length > 0;
      const entryType = isMinor ? "minor" : "adult";
      return selectedPatientTypes.includes(entryType as FilterPatientType);
    });

    return _.orderBy(
      filteredByType,
      (entry) => {
        const sortValue = entry.couple_members?.length
          ? entry.couple_members[0]["first_name"]
          : entry.patient["first_name"];
        return sortValue?.toLowerCase() || "";
      },
      [sortDirection],
    );
  }, [
    patientChartQuery.data,
    sortDirection,
    clientStatus,
    selectedPatientTypes,
  ]);

  const stats = useMemo(() => {
    const allStats = patientChartQuery.data?.pages[0]?.stats;
    if (!allStats) return allStats;

    return {
      ...allStats,
      total_count: patientChartEntries.length,
      total_patients: allStats.total_patients, // Keep total patients count as is
    };
  }, [patientChartQuery.data, patientChartEntries.length]);

  const clientListScrollContainerRef = useRef<HTMLDivElement>(null);

  const resetSearch = () => {
    setSearchTerm("");
  };

  const { user, defaultUserTimezone } = useUser();
  const { restrictAccessToPaidFeature } = useSubscription();
  const { isPhonePortrait } = usePhonePortraitContext();
  const navigate = useNavigate();

  useEffect(() => {
    navigate(
      `/pro-dashboard/patient-charts?tab=${patientType}&client-status=${clientStatus.join(
        ",",
      )}&patient-type-filter=${selectedPatientTypes.join(",")}`,
      { replace: true }
    );
  }, [navigate, patientType, clientStatus, selectedPatientTypes]);

  const [error, setError] = useState<any>();

  const updatePatientStatus = async (patientUserId, status) => {
    try {
      await putPatientProfile(patientUserId, {
        client_status: status,
      });
      refreshClients();
    } catch (error) {
      setError(error);
    }
  };

  const getDropdownActions = (patient, isMinor, isCouple) => {
    const patientStatus = patient.client_status;

    const patientActions = [
      isMinor || isCouple
        ? null
        : {
            label: "Add this patient to a Couple",
            actionLabel: ADD_PATIENT_TO_COUPLE_DROPDOWN_ITEM_LABEL,
            onClick: () => {
              setDefaultType("couple");
              setCoupleOrContactUser(patient);
              setShowPatientCreator(true);
            },
          },
      ENABLE_MERGE_CLIENTS &&
        !isCouple && {
          label: "Merge this profile with another client",
          actionLabel: MERGE_WITH_ANOTHER_PATIENT_DROPDOWN_ITEM_LABEL,
          onClick: () => {
            console.log("merge with another patient");
          },
        },
      ENABLE_SWITCH_CLIENT_TYPE &&
        !isCouple && {
          label: "Switch to {Adult or Minor}",
          actionLabel: SWITCH_CLIENT_TYPE_DROPDOWN_ITEM_LABEL,
          onClick: () => {
            console.log("switch client type");
          },
        },
      {
        label: "Mark as waitlist",
        actionLabel: MARK_AS_WAITLIST_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "waitlist"),
      },
      {
        label: "Mark as discharged",
        actionLabel: MARK_AS_DISCHARGED_DROPDOWN_ITEM_LABEL,
        onClick: () =>
          updatePatientStatus(patient.orchid_user_id, "discharged"),
      },
      {
        label: "Mark as inactive",
        actionLabel: MARK_AS_INACTIVE_DROPDOWN_ITEM_LABEL,
        className: styles["patient-charts__mark-inactive"],
        onClick: () => updatePatientStatus(patient.orchid_user_id, "inactive"),
      },
    ];

    const waitlistActions = [
      {
        label: "Mark as active",
        actionLabel: MARK_AS_ACTIVE_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "patient"),
      },
      {
        label: "Mark as discharged",
        actionLabel: MARK_AS_DISCHARGED_DROPDOWN_ITEM_LABEL,
        onClick: () =>
          updatePatientStatus(patient.orchid_user_id, "discharged"),
      },
      {
        label: "Mark as inactive",
        actionLabel: MARK_AS_INACTIVE_DROPDOWN_ITEM_LABEL,
        className: styles["patient-charts__mark-inactive"],
        onClick: () => updatePatientStatus(patient.orchid_user_id, "inactive"),
      },
    ];

    const inactiveActions = [
      {
        label: "Mark as active",
        actionLabel: MARK_AS_ACTIVE_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "patient"),
      },
      {
        label: "Mark as waitlist",
        actionLabel: MARK_AS_WAITLIST_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "waitlist"),
      },
      {
        label: "Mark as discharged",
        actionLabel: MARK_AS_DISCHARGED_DROPDOWN_ITEM_LABEL,
        onClick: () =>
          updatePatientStatus(patient.orchid_user_id, "discharged"),
      },
    ];

    const dischargedActions = [
      {
        label: "Mark as active",
        actionLabel: MARK_AS_ACTIVE_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "patient"),
      },
      {
        label: "Mark as waitlist",
        actionLabel: MARK_AS_WAITLIST_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "waitlist"),
      },
      {
        label: "Mark as inactive",
        actionLabel: MARK_AS_INACTIVE_DROPDOWN_ITEM_LABEL,
        onClick: () => updatePatientStatus(patient.orchid_user_id, "inactive"),
      },
    ];

    switch (patientStatus) {
      case "patient":
        return patientActions;
      case "waitlist":
        return waitlistActions;
      case "inactive":
        return inactiveActions;
      case "discharged":
        return dischargedActions;
      default:
        return patientActions;
    }
  };

  const renderActionsDropdown = (patient, isMinor, isCouple) => (
    <ActionsDropdown drop={"start"}>
      {getDropdownActions(patient, isMinor, isCouple).map((action, idx) => {
        return !action ? null : (
          <Dropdown.Item
            key={idx}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, action.actionLabel, "click");
              action.onClick();
            }}
            className={action.className || ""}
          >
            {action.label}
          </Dropdown.Item>
        );
      })}
    </ActionsDropdown>
  );

  // create patient button
  const [showPatientCreator, setShowPatientCreator] = useState(false);
  const [defaultType, setDefaultType] = useState("adult");
  const [coupleOrContactUser, setCoupleOrContactUser] =
    useState<PatientChartContact>();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [hoveredContact, setHoveredContact] = useState<string | null>(null);
  const [copiedContact, setCopiedContact] = useState<string | null>(null);
  const [hoveredClinicianDropdown, setHoveredClinicianDropdown] = useState<
    string | null
  >(null);
  const clinicianDropdownRef = useRef<HTMLDivElement>(null);

  const copyToClipboard = async (text: string, contactId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedContact(contactId);
      setTimeout(() => {
        setCopiedContact(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        clinicianDropdownRef.current &&
        !clinicianDropdownRef.current.contains(event.target as Node)
      ) {
        setHoveredClinicianDropdown(null);
      }
    };

    if (hoveredClinicianDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hoveredClinicianDropdown]);

  return (
    <div
      className={
        !isPhonePortrait
          ? styles["patient-charts"]
          : styles["patient-charts--phone"]
      }
    >
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}

      {showPatientCreator && (
        <PatientCreatorContainer
          show={true}
          onHide={() => {
            setCoupleOrContactUser(undefined);
            setDefaultType("adult");
            setShowPatientCreator(false);
          }}
          nextAction={() => refreshClients()}
          defaultType={defaultType}
          coupleOrContactUser={coupleOrContactUser}
          amplitudePageName={AMPLITUDE_PAGE_NAME}
        />
      )}

      <div className={styles["patient-charts__header"]}>
        <div className={styles["patient-charts__header-title-row"]}>
          <h2 className={styles["patient-charts__header-title"]}>
            Clients and Contacts
          </h2>
          <div className={"d-flex align-items-center"}>
            {restrictAccessToPaidFeature(user) ? null : (
              <Button
                className={styles["patient-charts__create-new-button"]}
                onClick={() => {
                  setError(undefined);
                  setShowPatientCreator(true);
                  track(AMPLITUDE_PAGE_NAME, "create-patient-button", "click");
                }}
              >
                <OutlinePlus16 />
                Create New
              </Button>
            )}
            <label className={styles["patient-charts__search"]}>
              <InputGroup>
                <InputGroup.Text>
                  <MagnifyingGlass />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    debouncedTrack(
                      AMPLITUDE_PAGE_NAME,
                      "search-by-patient-name",
                      "keypress",
                    );
                  }}
                  placeholder="Search Client or Contact name here"
                />
              </InputGroup>
            </label>
          </div>
        </div>
        <div className={styles["patient-charts__header-tabs-container"]}>
          <Tabs
            activeKey={patientType}
            id="patient-charts-tabs-controller"
            onSelect={(k) => {
              track(AMPLITUDE_PAGE_NAME, `${k}-tab`, "click");
              setPatientType((s) => (k as PATIENT_TYPE) || s);
              resetSearch();
            }}
            className={styles["patient-charts__tabs"]}
          >
            {restrictAccessToPaidFeature(user)
              ? Object.values(PATIENT_CONTACT_TAB_AMHC).map((el) => (
                  <Tab
                    key={el.id}
                    eventKey={el.id}
                    title={el.titleStringId}
                    tabClassName={styles["patient-charts__tab"]}
                  ></Tab>
                ))
              : Object.values(PATIENT_CONTACT_TABS).map((el) => (
                  <Tab
                    key={el.id}
                    eventKey={el.id}
                    title={el.titleStringId}
                    tabClassName={styles["patient-charts__tab"]}
                  ></Tab>
                ))}
          </Tabs>
          <div className={styles["patient-charts__header-user-row"]}>
            <OutlineUsersMore />
            <span className={"ml-2 gray01 d-flex align-items-center"}>
              {patientChartQuery.isLoading ? (
                <Spinner animation="border" size={"sm"} className={"mr-2"} />
              ) : (
                stats?.total_patients ?? 0
              )}{" "}
              Clients |
            </span>

            <div className={getClasses("d-flex ml-2 align-items-center")}>
              <div
                className={getClasses(
                  styles["patient-charts__user-text"],
                  clientStatus.includes("patient") && clientStatus.length === 1
                    ? styles["patient-charts__user-text--green"]
                    : clientStatus.includes("waitlist") &&
                      clientStatus.length === 1
                    ? styles["patient-charts__user-text--orange"]
                    : clientStatus.includes("inactive") &&
                      clientStatus.length === 1
                    ? styles["patient-charts__user-text--red"]
                    : styles["patient-charts__user-text--gray"],
                )}
              >
                {patientChartQuery.isLoading ? (
                  <Spinner animation="border" size={"sm"} />
                ) : (
                  patientChartEntries.length
                )}{" "}
                {clientStatus.includes("all")
                  ? "All"
                  : clientStatus.length === 1
                  ? clientStatus[0] === "patient"
                    ? "Active"
                    : capitalize(clientStatus[0])
                  : clientStatus.length === 4 &&
                    ["patient", "waitlist", "inactive", "discharged"].every(
                      (s) => clientStatus.includes(s as FilterStatus),
                    )
                  ? "All"
                  : clientStatus
                      .map((status) =>
                        status === "patient" ? "Active" : capitalize(status),
                      )
                      .join(", ")}{" "}
              </div>
              <div
                className={styles["patient-charts__active-user-dropdown"]}
                ref={statusDropdownRef}
              >
                <Button
                  variant="link"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                >
                  <OutlineChevronDown />
                </Button>
                {showStatusDropdown && (
                  <div
                    className={styles["patient-charts__custom-dropdown-menu"]}
                  >
                    {statuses.map(({ status, label, trackLabel }) => (
                      <div key={label}>
                        <div
                          className={styles["patient-charts__status-filter"]}
                          onClick={() => {
                            track(AMPLITUDE_PAGE_NAME, trackLabel, "click");
                            toggleClientStatus(status);
                          }}
                        >
                          <input
                            type="checkbox"
                            className={styles["patient-charts__checkbox"]}
                            checked={
                              status === "all"
                                ? [
                                    "patient",
                                    "waitlist",
                                    "inactive",
                                    "discharged",
                                  ].every((s) =>
                                    clientStatus.includes(s as FilterStatus),
                                  )
                                : clientStatus.includes(status)
                            }
                            onChange={() => {}}
                          />
                          {label}
                        </div>
                        {status === "all" && <hr className="m-0" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {patientChartQuery.isSuccess && patientChartEntries.length === 0 && (
        <div className={styles["patient-charts__empty-state"]}>
          <LazyImage
            src={PATIENT_CHARTS_EMPTY_STATE}
            width={201}
            height={"auto"}
          />
          <div className="mt-4 mb-2 bd-18">Your client list is empty.</div>
          <div className={"gray01"}>
            Use the <b>+Create New</b> button to add an individual, couple, or
            family client.
          </div>
        </div>
      )}

      {patientChartEntries.length > 0 && (
        <div
          id={"TABLE_CONTAINER"}
          className={styles["patient-charts__table-container"]}
          ref={clientListScrollContainerRef}
        >
          <Table className={styles["patient-charts__table"]}>
            <thead>
              <tr className={styles["patient-charts__table-header-row"]}>
                <th
                  className={styles["patient-charts__table-col-name--header"]}
                >
                  <span className={styles["patient-charts__client-name"]}>
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        disabled
                        onClick={(e) => e.stopPropagation()}
                      />
                      Client
                      <Dropdown className={styles["patient-charts__dropdown"]}>
                        <Dropdown.Toggle
                          variant="link"
                          bsPrefix="p-0 d-flex ml-2"
                        >
                          <OutlineChevronDown16 />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              setSortDirection("asc");
                              track(
                                AMPLITUDE_PAGE_NAME,
                                "first-name-asc-filter",
                                "click",
                              );
                            }}
                          >
                            <OutlineArrowUp16 />
                            Ascending (A-Z)
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortDirection("desc");
                              track(
                                AMPLITUDE_PAGE_NAME,
                                "first-name-desc-filter",
                                "click",
                              );
                            }}
                          >
                            <OutlineArrowDown16 />
                            Descending (Z-A)
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </span>
                </th>
                <th className={styles["patient-charts__table-col-type"]}>
                  <div className="d-flex align-items-center">
                    Type
                    <Dropdown className={styles["patient-charts__dropdown"]}>
                      <Dropdown.Toggle
                        variant="link"
                        bsPrefix="p-0 d-flex ml-2"
                      >
                        <OutlineChevronDown16 />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {patientTypes.map(({ type, label, trackLabel }) => (
                          <Dropdown.Item
                            key={label}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              track(AMPLITUDE_PAGE_NAME, trackLabel, "click");
                              togglePatientType(type);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPatientTypes.includes(type)}
                              onChange={() => {}}
                              className={styles["patient-charts__checkbox"]}
                            />
                            {label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </th>
                <th className={styles["patient-charts__table-col-status"]}>
                  Status
                </th>
                <th className={styles["patient-charts__table-col-dob"]}>DOB</th>
                <th className={styles["patient-charts__table-col-contact"]}>
                  Contact Details
                </th>
                <th className={styles["patient-charts__table-col-clinician"]}>
                  Clinician
                </th>
                <th className={styles["patient-charts__table-col-other-group"]}>
                  Other Relationship
                </th>
                <th
                  className={styles["patient-charts__table-col-action--header"]}
                ></th>
              </tr>
            </thead>
          </Table>
          <Table className={styles["patient-charts__table"]}>
            <tbody>
              {patientChartEntries.map(
                (
                  { guardians, couple_members, patient, couple_memberships },
                  i,
                ) => {
                  const redirectUrl = `/pro-dashboard/patient-charts/${patient.orchid_user_id}`;

                  const isMinor = guardians?.length && guardians.length > 0;
                  const isCouple = couple_members && couple_members.length > 0;

                  return isCouple && couple_members ? (
                    // couple tab goes first
                    <tr key={`{couple-${patient.orchid_user_id}-${i}}`}>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-name"]}
                      >
                        <div className="d-flex align-items-start">
                          <input
                            type="checkbox"
                            className="mr-2 mt-1"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div>
                            <div>
                              {couple_members[0].first_name}{" "}
                              {couple_members[0].last_name}
                            </div>
                            {couple_members[0].email &&
                            couple_members[0].phone_number ? (
                              <div
                                className={
                                  styles["patient-charts__large-spacing"]
                                }
                              ></div>
                            ) : (
                              <div
                                className={
                                  styles["patient-charts__small-spacing"]
                                }
                              ></div>
                            )}
                            <div>
                              {couple_members[1].first_name}{" "}
                              {couple_members[1].last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-type"]}
                      >
                        <PatientTypeTag type={"couple"} />
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-status"]}
                      >
                        <ClientStatusTag
                          status={getValidClientStatus(patient.client_status)}
                        />
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-dob"]}
                      >
                        {!couple_members[0].dob
                          ? "-"
                          : moment
                              .tz(couple_members[0].dob, defaultUserTimezone)
                              .format(DOB_FORMAT)}

                        {couple_members[0].email &&
                        couple_members[0].phone_number ? (
                          <div
                            className={styles["patient-charts__large-spacing"]}
                          ></div>
                        ) : !couple_members[0].email &&
                          !couple_members[0].phone_number ? null : (
                          <div
                            className={styles["patient-charts__small-spacing"]}
                          ></div>
                        )}
                        {!couple_members[1].dob
                          ? "-"
                          : moment
                              .tz(couple_members[1].dob, defaultUserTimezone)
                              .format(DOB_FORMAT)}
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-contact"]}
                      >
                        {!couple_members[0].email &&
                        !couple_members[0].phone_number ? (
                          <div>-</div>
                        ) : (
                          <>
                            <div
                              className="d-flex align-items-center position-relative"
                              onMouseEnter={() =>
                                setHoveredContact(
                                  `couple-0-email-${patient.orchid_user_id}`,
                                )
                              }
                              onMouseLeave={() => setHoveredContact(null)}
                            >
                              <span
                                className={
                                  styles["patient-charts__contact-text"]
                                }
                              >
                                {couple_members[0].email}
                              </span>
                              {hoveredContact ===
                                `couple-0-email-${patient.orchid_user_id}` &&
                                (copiedContact ===
                                `couple-0-email-${patient.orchid_user_id}` ? (
                                  <OutlineCopied20
                                    className={getClasses(
                                      "ml-2 text-success",
                                      styles["patient-charts__copy-icon"],
                                    )}
                                  />
                                ) : (
                                  <OutlineCopy20
                                    className={getClasses(
                                      "ml-2 cursor-pointer",
                                      styles["patient-charts__copy-icon"],
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (couple_members[0].email) {
                                        copyToClipboard(
                                          couple_members[0].email,
                                          `couple-0-email-${patient.orchid_user_id}`,
                                        );
                                      }
                                    }}
                                  />
                                ))}
                            </div>
                            {couple_members[0].phone_number && (
                              <div
                                className="d-flex align-items-center position-relative mt-2"
                                onMouseEnter={() =>
                                  setHoveredContact(
                                    `couple-0-phone-${patient.orchid_user_id}`,
                                  )
                                }
                                onMouseLeave={() => setHoveredContact(null)}
                              >
                                {couple_members[0].phone_number}
                                {hoveredContact ===
                                  `couple-0-phone-${patient.orchid_user_id}` &&
                                  (copiedContact ===
                                  `couple-0-phone-${patient.orchid_user_id}` ? (
                                    <OutlineCopied20
                                      className={getClasses(
                                        "ml-2 text-success",
                                        styles["patient-charts__copy-icon"],
                                      )}
                                    />
                                  ) : (
                                    <OutlineCopy20
                                      className={getClasses(
                                        "ml-2 cursor-pointer",
                                        styles["patient-charts__copy-icon"],
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (couple_members[0].phone_number) {
                                          copyToClipboard(
                                            couple_members[0].phone_number!,
                                            `couple-0-phone-${patient.orchid_user_id}`,
                                          );
                                        }
                                      }}
                                    />
                                  ))}
                              </div>
                            )}
                          </>
                        )}
                        <div
                          className={styles["patient-charts__small-spacing"]}
                        ></div>
                        {!couple_members[1].email &&
                        !couple_members[1].phone_number ? (
                          <div>-</div>
                        ) : (
                          <>
                            <div
                              className="d-flex align-items-center position-relative"
                              onMouseEnter={() =>
                                setHoveredContact(
                                  `couple-1-email-${patient.orchid_user_id}`,
                                )
                              }
                              onMouseLeave={() => setHoveredContact(null)}
                            >
                              <span
                                className={
                                  styles["patient-charts__contact-text"]
                                }
                              >
                                {couple_members[1].email}
                              </span>
                              {hoveredContact ===
                                `couple-1-email-${patient.orchid_user_id}` &&
                                (copiedContact ===
                                `couple-1-email-${patient.orchid_user_id}` ? (
                                  <OutlineCopied20
                                    className={getClasses(
                                      "ml-2 text-success",
                                      styles["patient-charts__copy-icon"],
                                    )}
                                  />
                                ) : (
                                  <OutlineCopy20
                                    className={getClasses(
                                      "ml-2 cursor-pointer",
                                      styles["patient-charts__copy-icon"],
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (couple_members[1].email) {
                                        copyToClipboard(
                                          couple_members[1].email,
                                          `couple-1-email-${patient.orchid_user_id}`,
                                        );
                                      }
                                    }}
                                  />
                                ))}
                            </div>
                            {couple_members[1].phone_number && (
                              <div
                                className="d-flex align-items-center position-relative mt-2"
                                onMouseEnter={() =>
                                  setHoveredContact(
                                    `couple-1-phone-${patient.orchid_user_id}`,
                                  )
                                }
                                onMouseLeave={() => setHoveredContact(null)}
                              >
                                {couple_members[1].phone_number}
                                {hoveredContact ===
                                  `couple-1-phone-${patient.orchid_user_id}` &&
                                  (copiedContact ===
                                  `couple-1-phone-${patient.orchid_user_id}` ? (
                                    <OutlineCopied20
                                      className={getClasses(
                                        "ml-2 text-success",
                                        styles["patient-charts__copy-icon"],
                                      )}
                                    />
                                  ) : (
                                    <OutlineCopy20
                                      className={getClasses(
                                        "ml-2 cursor-pointer",
                                        styles["patient-charts__copy-icon"],
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (couple_members[1].phone_number) {
                                          copyToClipboard(
                                            couple_members[1].phone_number!,
                                            `couple-1-phone-${patient.orchid_user_id}`,
                                          );
                                        }
                                      }}
                                    />
                                  ))}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      {!ENABLE_MULTIPLE_CLINICIANS ? (
                        <td
                          className={
                            styles["patient-charts__table-col-clinician"]
                          }
                        >
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${user?.sub}`}>
                                {user?.first_name || ""} {user?.last_name || ""}
                              </Tooltip>
                            }
                          >
                            <img
                              src={apiHostAndPath(
                                `/api/users/orchid_pro/profile_image/${1}`,
                              )}
                              alt={user?.first_name || ""}
                              className={styles["patient-charts__img"]}
                              onError={(e) => {
                                (e.target as HTMLImageElement).onerror = null;
                                (e.target as HTMLImageElement).src =
                                  "/img/placeholder/profile.png";
                              }}
                            />
                          </OverlayTrigger>
                        </td>
                      ) : (
                        <td
                          className={
                            styles["patient-charts__table-col-clinician"]
                          }
                        >
                          {CLINICIANS.slice(0, 3).map((clinician, index) => (
                            <OverlayTrigger
                              key={index}
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`tooltip-${clinician.orchid_pro_user_id}`}
                                >
                                  {clinician.name}
                                </Tooltip>
                              }
                            >
                              <img
                                src={apiHostAndPath(
                                  `/api/users/orchid_pro/profile_image/${clinician.orchid_pro_user_id}`,
                                )}
                                alt={clinician.name}
                                className={styles["patient-charts__img"]}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).onerror = null;
                                  (e.target as HTMLImageElement).src =
                                    "/img/placeholder/profile.png";
                                }}
                              />
                            </OverlayTrigger>
                          ))}
                          {CLINICIANS.length > 3 && (
                            <div
                              className={
                                styles["patient-charts__clinician-dropdown"]
                              }
                              ref={clinicianDropdownRef}
                              onMouseEnter={() =>
                                setHoveredClinicianDropdown(
                                  `couple-${patient.orchid_user_id}`,
                                )
                              }
                              onMouseLeave={() =>
                                setHoveredClinicianDropdown(null)
                              }
                            >
                              <div
                                className={
                                  styles["patient-charts__img"] +
                                  " " +
                                  styles["patient-charts__more-indicator"]
                                }
                              >
                                +{CLINICIANS.length - 3}
                              </div>
                              {hoveredClinicianDropdown ===
                                `couple-${patient.orchid_user_id}` && (
                                <div
                                  className={
                                    styles[
                                      "patient-charts__clinician-dropdown-menu"
                                    ]
                                  }
                                >
                                  {CLINICIANS.map((clinician, index) => (
                                    <div
                                      key={index}
                                      className={
                                        styles["patient-charts__clinician-item"]
                                      }
                                    >
                                      <img
                                        src={apiHostAndPath(
                                          `/api/users/orchid_pro/profile_image/${clinician.orchid_pro_user_id}`,
                                        )}
                                        alt={clinician.name}
                                        className={
                                          styles["patient-charts__img"]
                                        }
                                        onError={(e) => {
                                          (
                                            e.target as HTMLImageElement
                                          ).onerror = null;
                                          (e.target as HTMLImageElement).src =
                                            "/img/placeholder/profile.png";
                                        }}
                                      />
                                      <span className="ml-2">
                                        {clinician.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      <td
                        className={
                          styles["patient-charts__table-col-other-group"]
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "couple-row", "click");
                          navigate(redirectUrl);
                        }}
                      >
                        <span
                          className={styles["patient-charts__tags-container"]}
                        >
                          {couple_memberships?.length &&
                          couple_memberships.length > 0 ? (
                            <PatientTypeTag type={"couple"} />
                          ) : null}
                          {ENABLE_FAMILY_TAG && (
                            <div className={styles["family-tag"]}>Family</div>
                          )}
                        </span>
                      </td>
                      <td
                        className={styles["patient-charts__table-col-action"]}
                      >
                        {renderActionsDropdown(patient, isMinor, isCouple)}
                      </td>
                    </tr>
                  ) : (
                    <tr key={`{client-${patient.orchid_user_id}-${i}}`}>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type": isMinor ? "minor" : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-name"]}
                      >
                        <div className="d-flex align-items-start">
                          <input
                            type="checkbox"
                            className="mr-2 mt-1"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div>
                            {patient.first_name} {patient.last_name}
                          </div>
                        </div>
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type": isMinor ? "minor" : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-type"]}
                      >
                        <PatientTypeTag type={isMinor ? "minor" : "adult"} />
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type": isMinor ? "minor" : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-status"]}
                      >
                        <ClientStatusTag
                          status={getValidClientStatus(patient.client_status)}
                        />
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type": isMinor ? "minor" : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-dob"]}
                      >
                        {!patient.dob
                          ? "-"
                          : moment
                              .tz(patient.dob, defaultUserTimezone)
                              .format(DOB_FORMAT)}
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type": isMinor ? "minor" : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                        className={styles["patient-charts__table-col-contact"]}
                      >
                        {isMinor && !!guardians ? (
                          <>
                            {/* for minor who has guardians */}
                            {(patient.email || patient.phone_number) && (
                              <div className={"mb-4"}>
                                {patient.email && (
                                  <div
                                    className="d-flex align-items-center position-relative mb-2"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `patient-email-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    <span
                                      className={
                                        styles["patient-charts__contact-text"]
                                      }
                                    >
                                      {patient.email}
                                    </span>
                                    {hoveredContact ===
                                      `patient-email-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `patient-email-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (patient.email) {
                                              copyToClipboard(
                                                patient.email,
                                                `patient-email-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                                {patient.phone_number && (
                                  <div
                                    className="d-flex align-items-center position-relative"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `patient-phone-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    {patient.phone_number}
                                    {hoveredContact ===
                                      `patient-phone-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `patient-phone-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (patient.phone_number) {
                                              copyToClipboard(
                                                patient.phone_number,
                                                `patient-phone-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                              </div>
                            )}

                            <div className={"mb-2 deep-blue"}>
                              {guardians[0].contact.first_name}{" "}
                              {guardians[0].contact.last_name}
                            </div>
                            {!guardians[0].contact.email &&
                            !guardians[0].contact.phone_number ? null : (
                              <>
                                {guardians[0].contact.email && (
                                  <div
                                    className="d-flex align-items-center position-relative mb-2"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `guardian-email-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    <span
                                      className={
                                        styles["patient-charts__contact-text"]
                                      }
                                    >
                                      {guardians[0].contact.email}
                                    </span>
                                    {hoveredContact ===
                                      `guardian-email-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `guardian-email-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              guardians?.[0]?.contact?.email
                                            ) {
                                              copyToClipboard(
                                                guardians[0].contact.email,
                                                `guardian-email-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                                {guardians[0].contact.phone_number && (
                                  <div
                                    className="d-flex align-items-center position-relative"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `guardian-phone-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    {guardians[0].contact.phone_number}
                                    {hoveredContact ===
                                      `guardian-phone-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `guardian-phone-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              guardians?.[0]?.contact
                                                ?.phone_number
                                            ) {
                                              copyToClipboard(
                                                guardians[0].contact
                                                  .phone_number,
                                                `guardian-phone-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {/* for adult */}
                            {!patient.email && !patient.phone_number ? (
                              <div>-</div>
                            ) : (
                              <>
                                {patient.email && (
                                  <div
                                    className="d-flex align-items-center position-relative mb-2"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `patient-email-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    <span
                                      className={
                                        styles["patient-charts__contact-text"]
                                      }
                                    >
                                      {patient.email}
                                    </span>
                                    {hoveredContact ===
                                      `patient-email-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `patient-email-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (patient.email) {
                                              copyToClipboard(
                                                patient.email,
                                                `patient-email-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                                {patient.phone_number && (
                                  <div
                                    className="d-flex align-items-center position-relative"
                                    onMouseEnter={() =>
                                      setHoveredContact(
                                        `patient-phone-${patient.orchid_user_id}`,
                                      )
                                    }
                                    onMouseLeave={() => setHoveredContact(null)}
                                  >
                                    {patient.phone_number}
                                    {hoveredContact ===
                                      `patient-phone-${patient.orchid_user_id}` &&
                                      (copiedContact ===
                                      `patient-phone-${patient.orchid_user_id}` ? (
                                        <OutlineCopied20
                                          className={getClasses(
                                            "ml-2 text-success",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                        />
                                      ) : (
                                        <OutlineCopy20
                                          className={getClasses(
                                            "ml-2 cursor-pointer",
                                            styles["patient-charts__copy-icon"],
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (patient.phone_number) {
                                              copyToClipboard(
                                                patient.phone_number,
                                                `patient-phone-${patient.orchid_user_id}`,
                                              );
                                            }
                                          }}
                                        />
                                      ))}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </td>
                      {!ENABLE_MULTIPLE_CLINICIANS ? (
                        <td
                          className={
                            styles["patient-charts__table-col-clinician"]
                          }
                        >
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${user?.sub}`}>
                                {user?.first_name || ""} {user?.last_name || ""}
                              </Tooltip>
                            }
                          >
                            <img
                              src={apiHostAndPath(
                                `/api/users/orchid_pro/profile_image/${user?.sub}`,
                              )}
                              alt={user?.first_name || ""}
                              className={styles["patient-charts__img"]}
                              onError={(e) => {
                                (e.target as HTMLImageElement).onerror = null;
                                (e.target as HTMLImageElement).src =
                                  "/img/placeholder/profile.png";
                              }}
                            />
                          </OverlayTrigger>
                        </td>
                      ) : (
                        <td
                          className={
                            styles["patient-charts__table-col-clinician"]
                          }
                        >
                          {CLINICIANS.slice(0, 3).map((clinician, index) => (
                            <OverlayTrigger
                              key={index}
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`tooltip-${clinician.orchid_pro_user_id}`}
                                >
                                  {clinician.name}
                                </Tooltip>
                              }
                            >
                              <img
                                src={apiHostAndPath(
                                  `/api/users/orchid_pro/profile_image/${clinician.orchid_pro_user_id}`,
                                )}
                                alt={clinician.name}
                                className={styles["patient-charts__img"]}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).onerror = null;
                                  (e.target as HTMLImageElement).src =
                                    "/img/placeholder/profile.png";
                                }}
                              />
                            </OverlayTrigger>
                          ))}
                          {CLINICIANS.length > 3 && (
                            <div
                              className={
                                styles["patient-charts__clinician-dropdown"]
                              }
                              ref={clinicianDropdownRef}
                              onMouseEnter={() =>
                                setHoveredClinicianDropdown(
                                  `patient-${patient.orchid_user_id}`,
                                )
                              }
                              onMouseLeave={() =>
                                setHoveredClinicianDropdown(null)
                              }
                            >
                              <div
                                className={
                                  styles["patient-charts__img"] +
                                  " " +
                                  styles["patient-charts__more-indicator"]
                                }
                              >
                                +{CLINICIANS.length - 3}
                              </div>
                              {hoveredClinicianDropdown ===
                                `patient-${patient.orchid_user_id}` && (
                                <div
                                  className={
                                    styles[
                                      "patient-charts__clinician-dropdown-menu"
                                    ]
                                  }
                                >
                                  {CLINICIANS.map((clinician, index) => (
                                    <div
                                      key={index}
                                      className={
                                        styles["patient-charts__clinician-item"]
                                      }
                                    >
                                      <img
                                        src={apiHostAndPath(
                                          `/api/users/orchid_pro/profile_image/${clinician.orchid_pro_user_id}`,
                                        )}
                                        alt={clinician.name}
                                        className={
                                          styles["patient-charts__img"]
                                        }
                                        onError={(e) => {
                                          (
                                            e.target as HTMLImageElement
                                          ).onerror = null;
                                          (e.target as HTMLImageElement).src =
                                            "/img/placeholder/profile.png";
                                        }}
                                      />
                                      <span className="ml-2">
                                        {clinician.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      <td
                        className={
                          styles["patient-charts__table-col-other-group"]
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          track(AMPLITUDE_PAGE_NAME, "patient-row", "click", {
                            eventProperties: {
                              "patient-type":
                                guardians?.length && guardians.length > 0
                                  ? "minor"
                                  : "adult",
                            },
                          });
                          navigate(redirectUrl);
                        }}
                      >
                        <span
                          className={styles["patient-charts__tags-container"]}
                        >
                          {couple_memberships?.length &&
                          couple_memberships.length > 0 ? (
                            <PatientTypeTag type={"couple"} />
                          ) : null}
                          {guardians?.length && guardians.length > 0 ? (
                            <div className={styles["contact-tag"]}>Contact</div>
                          ) : null}
                          {ENABLE_FAMILY_TAG && (
                            <div className={styles["family-tag"]}>Family</div>
                          )}
                        </span>
                      </td>
                      <td
                        className={styles["patient-charts__table-col-action"]}
                      >
                        {renderActionsDropdown(patient, isMinor, isCouple)}
                      </td>
                    </tr>
                  );
                },
              )}
              {(patientChartQuery.isPending ||
                patientChartQuery.isFetchingNextPage) && (
                <tr key="loading-superbill">
                  <td colSpan={7} className="text-center">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PatientCharts;
